<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>Смена тарифа</v-card-title>
          <v-card-subtitle>
            Вы можете подать заявку на смену тарифа
          </v-card-subtitle>
          
          <v-card-text>
            <v-select
              v-model="selectedTariff"
              :items="tariffs"
              item-title="title"
              item-value="id"
              label="Выберите новый тариф"
              variant="outlined"
            />
          </v-card-text>

          <v-card-actions>
            <v-btn 
              color="primary"
              :disabled="!selectedTariff || submitting"
              :loading="submitting"
              @click="submitTariffChange"
            >
              Подать заявку
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card>
          <v-card-title>Роуминг</v-card-title>
          <v-card-text>
            <v-switch
              v-model="roamingEnabled"
              :label="roamingEnabled ? 'Роуминг включён' : 'Роуминг выключен'"
              color="success"
              :loading="submitting"
              :disabled="submitting"
              hide-details
              @update:model-value="submitRoaming"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import axios from 'axios'
import { useUserStore } from '@/stores/user'
import { API_BASE_URL } from '@/services/api'

interface Tariff {
  id: number
  Name: string
  group: string
}

const userStore = useUserStore()
const selectedTariff = ref(null)
const tariffs = ref([])
const submitting = ref(false)
const roamingEnabled = ref(false)
const snackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')

const getPhoneNumber = () => {
  return userStore.state?.context?.user?.phoneNumber?.substring(4) || ''
}

const showMessage = (text: string, color = 'success') => {
  snackbarText.value = text
  snackbarColor.value = color
  snackbar.value = true
}

const fetchTariffs = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/tariffs`)
    const items = response.data
    tariffs.value = items
      .filter((t: Tariff) => String(t.group) === '1')
      .map((item: Tariff) => ({
        title: item.Name || item.name,
        id: item.id
      }))
  } catch (error) {
    console.error('Error fetching tariffs:', error)
  }
}

const submitTariffChange = async () => {
  if (!selectedTariff.value) return
  submitting.value = true
  try {
    await axios.post(`${API_BASE_URL}/requests`, {
      phone: getPhoneNumber(),
      type: 'tariff_change',
      data: { tariff_id: selectedTariff.value }
    })
    showMessage('Заявка на смену тарифа отправлена')
    selectedTariff.value = null
  } catch (error: any) {
    const msg = error.response?.data?.error || 'Ошибка при отправке заявки'
    showMessage(msg, 'error')
  } finally {
    submitting.value = false
  }
}

const submitRoaming = async (newValue: boolean) => {
  submitting.value = true
  const type = newValue ? 'roaming_enable' : 'roaming_disable'
  try {
    await axios.post(`${API_BASE_URL}/requests`, {
      phone: getPhoneNumber(),
      type,
    })
    showMessage(newValue ? 'Заявка на включение роуминга отправлена' : 'Заявка на отключение роуминга отправлена')
  } catch (error: any) {
    const msg = error.response?.data?.error || 'Ошибка при отправке заявки'
    showMessage(msg, 'error')
    roamingEnabled.value = !newValue
  } finally {
    submitting.value = false
  }
}

const fetchOptions = async () => {
  const phone = getPhoneNumber()
  if (!phone) return
  try {
    const { data } = await axios.get(`${API_BASE_URL}/options?phone=${phone}`)
    roamingEnabled.value = data.roaming === '1'
  } catch (e) {
    console.error('Error fetching options:', e)
  }
}

watch(() => userStore.state.value, async (state) => {
  if (state === 'authenticated') {
    await Promise.all([fetchTariffs(), fetchOptions()])
  }
}, { immediate: true })
</script>

<style scoped>
.v-card {
  margin-bottom: 16px;
}
</style>
